import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  SkeletonText,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import { useEffect } from "react"
import { z } from "zod"

import { type UserPublic, UsersService } from "../../../client"
import AddUser from "../../../components/Admin/AddUser"
import ActionsMenuItems from "../../../components/Common/ActionsMenu.tsx"
import Navbar from "../../../components/Common/Navbar"
import { PaginationFooter } from "../../../components/Common/PaginationFooter.tsx"

const usersSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/admin/")({
  component: Admin,
  validateSearch: (search) => usersSearchSchema.parse(search),
})

const PER_PAGE = 7

function getUsersQueryOptions({ page }: { page: number }) {
  return {
    queryFn: () =>
      UsersService.readUsers({ skip: (page - 1) * PER_PAGE, limit: PER_PAGE }),
    queryKey: ["users", { page }],
  }
}

function UsersTable() {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) =>
    navigate({
      search: (current) => ({ ...current, page }),
    })

  const {
    data: users,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...getUsersQueryOptions({ page }),
    placeholderData: (prevData) => prevData,
  })

  const hasNextPage = !isPlaceholderData && users?.data.length === PER_PAGE
  const hasPreviousPage = page > 1
  const theadBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const tableCardBg = useColorModeValue("white", "gray.800")
  const tableCardBorder = useColorModeValue("gray.200", "whiteAlpha.200")

  useEffect(() => {
    if (hasNextPage) {
      queryClient.prefetchQuery(getUsersQueryOptions({ page: page + 1 }))
    }
  }, [page, queryClient, hasNextPage])

  return (
    <>
      <Box
        bg={tableCardBg}
        borderWidth="1px"
        borderColor={tableCardBorder}
        borderRadius="2xl"
        overflow="hidden"
      >
        <TableContainer width="100%">
          <Table size={{ base: "sm", md: "md" }} sx={{ tableLayout: "fixed" }}>
            <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th>Imię i nazwisko</Th>
                <Th>Akronim</Th>
                <Th>Email</Th>
                <Th>Rola</Th>
                <Th>Status</Th>
                <Th>Akcje</Th>
              </Tr>
            </Thead>
            {isPending ? (
              <Tbody>
                <Tr>
                  {new Array(6).fill(null).map((_, index) => (
                    <Td key={index}>
                      <SkeletonText noOfLines={1} paddingBlock="16px" />
                    </Td>
                  ))}
                </Tr>
              </Tbody>
            ) : (
              <Tbody>
                {users?.data.map((user, idx) => (
                  <Tr
                    key={user.ID}
                    bg={idx % 2 === 0 ? "transparent" : zebraBg}
                    _hover={{ bg: hoverBg }}
                  >
                    <Td
                      color={!user.fullName ? "ui.dim" : "inherit"}
                      isTruncated
                      maxWidth="150px"
                      fontWeight="medium"
                    >
                      {user.fullName || "Brak"}
                      {currentUser?.ID === user.ID && (
                        <Badge ml="1" colorScheme="teal">
                          Ty
                        </Badge>
                      )}
                    </Td>
                    <Td isTruncated maxWidth="150px" color={mutedText}>
                      {user.acronym}
                    </Td>
                    <Td isTruncated maxWidth="150px">
                      {user.email}
                    </Td>
                    <Td>
                      {Array.isArray(user.roles) && user.roles.length > 0 ? (
                        <Flex wrap="wrap" gap={1}>
                          <Badge colorScheme="green" variant="subtle">
                            {user.roles[0].code} ({user.roles[0].level})
                          </Badge>
                        </Flex>
                      ) : (
                        <Badge colorScheme="gray" variant="outline">
                          Brak ról
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      <Flex gap={2}>
                        <Box
                          w="2"
                          h="2"
                          borderRadius="50%"
                          bg={user.isActive ? "ui.success" : "ui.danger"}
                          alignSelf="center"
                        />
                        {user.isActive ? "Aktywny" : "Nieaktywny"}
                      </Flex>
                    </Td>
                    <Td>
                      <ActionsMenuItems
                        type="User"
                        value={user}
                        disabled={currentUser?.ID === user.ID}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            )}
          </Table>
        </TableContainer>
      </Box>
      <PaginationFooter
        onChangePage={setPage}
        page={page}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
      />
    </>
  )
}

function Admin() {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isIndex = pathname === "/admin"
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const statBg = useColorModeValue("white", "gray.800")
  const statBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const statLabelColor = useColorModeValue("gray.600", "gray.300")
  const statValueColor = useColorModeValue("gray.900", "white")

  const queryClient = useQueryClient()
  const { data: usersAll } = useQuery({
    queryKey: ["users", "stats"],
    queryFn: () => UsersService.readUsers({ skip: 0, limit: 1000 }),
    enabled: isIndex,
  })
  const allUsers = usersAll?.data ?? []
  const activeCount = allUsers.filter((u) => u.isActive).length
  const adminCount = allUsers.filter((u) =>
    (u.roles ?? []).some((r) => ["admin", "superadmin"].includes(r.level)),
  ).length
  const me = queryClient.getQueryData<UserPublic>(["currentUser"])

  return (
    <Container maxW="9xl" py={4}>
      {isIndex ? (
        <Box maxW="9xl" mx="auto" px={4}>
          <VStack spacing={4} align="stretch">
            <Box
              bgGradient="linear(to-r, slateblue, #2B6CB0)"
              color="white"
              borderRadius="2xl"
              px={{ base: 5, md: 8 }}
              py={{ base: 5, md: 7 }}
              boxShadow="lg"
            >
              <HStack
                justify="space-between"
                align={{ base: "flex-start", md: "center" }}
                flexWrap="wrap"
                gap={3}
              >
                <Box>
                  <Heading size="lg" textAlign={{ base: "left", md: "left" }}>
                    Zarządzanie użytkownikami
                  </Heading>
                  <Text mt={1} opacity={0.95}>
                    Zarządzaj kontami, rolami i dostępem do modułów systemu.
                  </Text>
                </Box>
                <Badge
                  bg="whiteAlpha.250"
                  color="white"
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  Zalogowany: {me?.email ?? "—"}
                </Badge>
              </HStack>
            </Box>

            <HStack spacing={3} align="stretch">
              <Stat
                bg={statBg}
                borderWidth="1px"
                borderColor={statBorder}
                borderRadius="xl"
                px={4}
                py={3}
              >
                <StatLabel color={statLabelColor}>
                  Wszyscy użytkownicy
                </StatLabel>
                <StatNumber color={statValueColor}>
                  {allUsers.length}
                </StatNumber>
              </Stat>
              <Stat
                bg={statBg}
                borderWidth="1px"
                borderColor={statBorder}
                borderRadius="xl"
                px={4}
                py={3}
              >
                <StatLabel color={statLabelColor}>Aktywni</StatLabel>
                <StatNumber color={statValueColor}>{activeCount}</StatNumber>
              </Stat>
              <Stat
                bg={statBg}
                borderWidth="1px"
                borderColor={statBorder}
                borderRadius="xl"
                px={4}
                py={3}
              >
                <StatLabel color={statLabelColor}>Admin/Superadmin</StatLabel>
                <StatNumber color={statValueColor}>{adminCount}</StatNumber>
              </Stat>
            </HStack>

            <Box
              bg={panelBg}
              borderWidth="1px"
              borderColor={panelBorder}
              borderRadius="2xl"
              p={{ base: 3, md: 4 }}
              boxShadow="sm"
            >
              <HStack
                justify="space-between"
                align="center"
                mb={3}
                flexWrap="wrap"
                gap={2}
              >
                <Text fontWeight="semibold">Zespół i uprawnienia</Text>
                <HStack>
                  <Button
                    variant="outline"
                    onClick={() => navigate({ to: "/admin/customers" })}
                  >
                    Klienci
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate({ to: "/admin/licenses" })}
                  >
                    Licencje
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate({ to: "/admin/roles", search: { page: 1 } })
                    }
                  >
                    Zarządzaj rolami
                  </Button>
                </HStack>
              </HStack>
              <Navbar type={"User"} addModalAs={AddUser} />
              <UsersTable />
            </Box>
          </VStack>
        </Box>
      ) : (
        <Outlet />
      )}
    </Container>
  )
}
